<i18n
  src="@/locales/views/organization/certifications/basic_certifications_producers/show_crop_field_certification.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/crop_field_certification.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_producer')"
    :params="{
      organization_id: organization_id,
      producer_id: producer_id,
    }"
  ></go-back-link>
  <bread-crumbs
    :labels="[module_name, producer?.organization_producer?.producer_code]"
  ></bread-crumbs>
  <producer-info v-if="load_data_general" :producer="producer"></producer-info>
  <template v-if="load_data_general">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">Datos de la certificación</h3>
      </div>

      <div class="panel-body">
        <div class="space-y-8 divide-y divide-slate-200">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <dt class="label-data">
                {{ $t("model.crop_field_certification.certification_id") }}
              </dt>
              <dd class="content-data">
                {{ crop_field_certification.certification.name }}
                <yes-no-label
                  :value="crop_field_certification.active"
                  yes="Activo"
                  no="Inactivo"
                />

                <span
                  v-if="crop_field_certification.suspended"
                  class="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ring-1 ring-inset bg-red-50 text-red-700 ring-red-600/20"
                >
                  Suspendido
                </span>
              </dd>
            </div>

            <div class="sm:col-span-3">
              <dt class="label-data">
                {{ $t("model.crop_field_certification.crop_field_id") }}
              </dt>
              <dd class="content-data">
                {{ cropFieldLabel(crop_field_certification.crop_field) }}
              </dd>
            </div>

            <div class="sm:col-span-3">
              <dt class="label-data">
                {{ $t("model.crop_field_certification.certification_code") }}
              </dt>
              <dd class="content-data">
                {{ crop_field_certification.certification_code }}
              </dd>
            </div>

            <div class="sm:col-span-3">
              <dt class="label-data">
                {{ $t("model.crop_field_certification.certification_number") }}
              </dt>
              <dd class="content-data">
                {{ crop_field_certification.certification_number }}
              </dd>
            </div>

            <div class="sm:col-span-3">
              <dt class="label-data">
                {{ $t("model.crop_field_certification.renewal_date") }}
              </dt>
              <dd class="content-data">
                {{ dateFormatter(crop_field_certification.renewal_date) }}
              </dd>
            </div>

            <div class="sm:col-span-3">
              <dt class="label-data">
                {{ $t("model.crop_field_certification.expiration_date") }}
              </dt>
              <dd class="content-data">
                {{ dateFormatter(crop_field_certification.expiration_date) }}
              </dd>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-6">
            <div class="sm:col-span-6">
              <dt class="label-data">
                {{
                  $t(
                    "model.crop_field_certification.crop_field_cert_organization.observations"
                  )
                }}
                <button type="button" @click.prevent="edit_observations = true">
                  <i class="fa-solid fa-pen" />
                </button>
              </dt>
              <dd v-if="!edit_observations" class="content-data">
                {{ crop_field_certification.cert_organization_observations }}
              </dd>
              <div v-if="edit_observations" class="mt-1">
                <textarea
                  v-model="
                    crop_field_certification.cert_organization_observations
                  "
                  id="crop_field_cert_organization_observations"
                  name="crop_field_cert_organization_observations"
                  rows="3"
                  class="shadow-sm focus:ring-green-500 focus:border-green-500 mt-1 block w-full sm:text-sm border border-slate-300 rounded-md"
                />
                <div class="mt-1 flex justify-end">
                  <white-button
                    @click.prevent="edit_observations = false"
                    :label="$t('labels.cancel_button')"
                  />
                  <span class="space-actions"></span>
                  <success-button
                    v-if="!proc_save_button"
                    @click.prevent="saveCropFieldCertOrganization()"
                    :label="$t('labels.save_button')"
                  />
                  <success-button-processing
                    v-else
                    :label="$t('labels.save_button')"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="panel-footer-actions">
          <destroy-button
            v-if="!crop_field_certification.proc_button_destroy"
            :disabled_button="false"
            @destroyEvent="destroyCropFieldCertification"
            :label="$t('labels.destroy_button_label')"
            :title="
              $t('labels.destroy_button_title', {
                certification_name: crop_field_certification.certification.name,
              })
            "
            :message="$t('labels.destroy_button_message')"
            :label_confirm="$t('labels.destroy_button_label_confirm')"
          />
          <destroy-button-processing
            v-else
            :label="$t('labels.destroy_button_label')"
          />
          <span class="space-actions"></span>
          <edit-button
            @click.prevent="goToEditCropFieldCertification"
            :label="$t('labels.edit_button')"
          />
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import Requester from "@/views/organization/requesters/certifications/basic_certifications_producers/manage_crop_field_certification.js";
import ProducerInfo from "@/components/views/organization/certifications/basic_certifications_producers/ProducerInfo.vue";

export default {
  name: "ShowCropFieldCertification",
  data() {
    return {
      load_data_general: false,
      proc_button_destroy: false,
      proc_save_button: false,
      edit_observations: false,
      producer: {},
      crop_field_certification: {},
      observations: "",
    };
  },
  components: {
    ProducerInfo,
  },
  props: {
    organization_id: { required: true },
    producer_id: { required: true },
    crop_field_certification_id: { required: true },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
  methods: {
    cropFieldLabel: function (crop_field) {
      let labels = [];
      labels.push(crop_field.name);
      labels.push(crop_field.organization_crop_field.crop_field_code);
      labels = labels.filter((label) => label);
      return labels.join(" - ");
    },

    async loadData() {
      await this.fetchProducer();
      await this.fetchCropFieldCertification();
      this.load_data_general = true;
    },
    async fetchProducer() {
      let response = await Requester.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    async fetchCropFieldCertification() {
      let response = await Requester.fetchCropFieldCertification(
        this.organization_id,
        this.producer_id,
        this.crop_field_certification_id
      );

      this.crop_field_certification = response.data;
    },

    async destroyCropFieldCertification() {
      this.crop_field_certification.proc_button_destroy = true;
      try {
        let response = await Requester.destroy(
          this.organization_id,
          this.producer_id,
          this.crop_field_certification.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowProducer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
          });
        }
      } finally {
        this.crop_field_certification.proc_button_destroy = false;
      }
    },

    async saveCropFieldCertOrganization() {
      this.proc_save_button = true;
      try {
        const response = await Requester.manageCropFieldCertOrganization(
          this.organization_id,
          this.crop_field_certification_id,
          {
            observations:
              this.crop_field_certification.cert_organization_observations,
          }
        );
        this.crop_field_certification.cert_organization_observations =
          response.data.observations;
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      } finally {
        this.proc_save_button = false;
        this.edit_observations = false;
      }
    },

    goToShowProducer() {
      this.$router.push({
        name: this.routeName("show_producer"),
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
        },
      });
    },
    goToEditCropFieldCertification() {
      this.$router.push({
        name: this.routeName("edit_crop_field_certification"),
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
          crop_field_certification_id: this.crop_field_certification_id,
        },
      });
    },
  },
};
</script>
